import { useCallback, useEffect, useRef } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HamburgerNavBar,
  type RouterTabPanelProps,
} from 'mro-component-library';
import { ALL_ROUTES } from '../../constants';
import { useAuth } from '../../contexts/AuthContext';
import ProfileMenu from '../../components/molecules/ProfileMenu';

const tabInfo: RouterTabPanelProps = {
  tabsList: [
    {
      to: ALL_ROUTES.LANDING_PAGE,
      label: 'Summary Overview',
      testId: 'landing-page-tab',
    },
    {
      to: ALL_ROUTES.HEALTH_SYSTEMWISE_SUMMARY_PAGE,
      label: 'Health System Breakdown',
      testId: 'health-systemwise-summary-tab',
    },
    {
      to: ALL_ROUTES.DASHBOARD,
      label: 'Request Details',
      testId: 'dashboard-tab',
    },
  ],
};

/**
 * Top navigation, shared with AdminPortal via `mro-component-library`'s
 * `HamburgerNavBar` — same active-tab styling, logo and app-shell chrome,
 * rather than a bespoke AppBar. No `customLogo` is passed, so it renders the
 * library's default MRO logo, matching AdminPortal exactly.
 */
const isLogo = (node: EventTarget | null): boolean => {
  const element = node as HTMLElement | null;
  return element?.tagName === 'IMG' && element.classList.contains('logo');
};

const Header = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const goHome = useCallback(
    () => navigate(ALL_ROUTES.LANDING_PAGE),
    [navigate],
  );

  /**
   * HamburgerNavBar renders the logo as a plain `<img class="logo">` with no
   * click handler and no `onLogoClick` prop - the library owns that markup, and
   * this app deliberately does not fork it. Delegating from a wrapping element
   * is therefore the only hook available.
   *
   * A bare `onClick` on the wrapper would make this mouse-only, since an `img`
   * is not focusable and so never receives a key event. The effect below
   * promotes the logo to a real control (focusable, announced as a button) so
   * keyboard and screen-reader users get the same navigation. It runs against
   * the library's DOM rather than JSX because that element is not ours to
   * render.
   */
  useEffect(() => {
    const logo = wrapperRef.current?.querySelector<HTMLElement>('img.logo');
    if (!logo) return;

    logo.tabIndex = 0;
    logo.setAttribute('role', 'button');
    logo.setAttribute('aria-label', 'Go to Summary Overview');
  }, []);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (isLogo(event.target)) goHome();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isLogo(event.target)) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    // Space would otherwise scroll the page.
    event.preventDefault();
    goHome();
  };

  return (
    <div
      ref={wrapperRef}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className='nav-logo-click-wrapper'
    >
      <HamburgerNavBar
        appTitle='AR Reporting Dashboard'
        tabInfo={tabInfo}
        endContent={<ProfileMenu onLogout={logout} />}
      />
    </div>
  );
};

export default Header;
